/*
  ck-offer-guard.js — sitewide offer-integrity guard.

  Problem it solves: a customer can add claim / airline offer items to the cart
  (each line is tagged with a hidden `_ck_offer` property — "claim:<CODE>" or
  "airline:<CODE>" — written by ck-claim-packages.liquid / ck-airline-offer.liquid),
  then reach checkout even though the underlying offer is no longer valid
  (expired / disabled / unknown / fully-redeemed) or two different offers are
  mixed in one cart. In every one of those cases the discount will NOT apply the
  way the customer expects, so we must NOT let them check out.

  What it does, on every page (it is rendered from ck-header.liquid):
    1. Reads the offer index emitted by snippets/ck-offer-guard.liquid.
       · claims are indexed by code_hash (SHA-256 of the lowercased code) — the
         plaintext code is deliberately never exposed in page source.
       · airlines are indexed by their (public) code.
    2. Reads the live cart (/cart.js) and collects the distinct `_ck_offer` tags.
    3. Decides whether the cart's offer state is valid:
         · 0 offer tags        → fine, normal checkout (guard stays inert).
         · 1 offer tag, valid  → fine.
         · 1 offer tag, invalid→ ISSUE (expired / disabled / unknown / used).
         · 2+ distinct tags    → ISSUE (one offer per order).
    4. On an ISSUE it sets html[data-ck-offer-issue] — the snippet's CSS then
       hides the header cart icon + the cart/drawer checkout buttons — renders a
       sitewide banner explaining the problem and the fix, and drops an inline
       note where each hidden checkout button was so the drawer/cart page also
       explain themselves.
    5. Recomputes live after any /cart mutation (fetch is hooked) and on
       page-show / tab-focus, so it clears the moment the cart is fixed.

  It never blocks a plain (offerless) cart, so ordinary checkout is untouched.
*/
(function () {
  'use strict';

  var dataEl = document.getElementById('ck-offer-guard-data');
  if (!dataEl) return;

  var INDEX;
  try { INDEX = JSON.parse(dataEl.textContent || '{}'); } catch (e) { INDEX = {}; }

  var CLAIMS   = INDEX.claims   || []; /* [{code_hash,status,valid_to,usage_limit,times_used}] */
  var AIRLINES = INDEX.airlines || []; /* [{code,name,status,valid_to,usage_limit,times_used}] */
  var CART_URL = INDEX.cart_url || '/cart';
  var WHATSAPP = (window.CK_WA && window.CK_WA.url) || INDEX.whatsapp || 'https://wa.me/6591002680';

  var claimByHash = {};
  CLAIMS.forEach(function (c) { if (c && c.code_hash) claimByHash[c.code_hash] = c; });
  var airlineByCode = {};
  AIRLINES.forEach(function (a) { if (a && a.code) airlineByCode[String(a.code).toLowerCase()] = a; });

  var root   = document.documentElement;
  var banner = document.getElementById('ck-offer-guard-banner');
  /* Offer pages own their own conflict / invalid messaging, so we suppress our
     banner there — but still gate the header cart icon + checkout buttons. */
  var onOfferPage = !!document.querySelector('.ck-claim, .ck-airline-offer');

  /* ── helpers ─────────────────────────────────────────────────────── */
  function sha256hex(str) {
    if (!(window.crypto && window.crypto.subtle)) return Promise.resolve(null);
    try {
      var buf = new TextEncoder().encode(str);
      return window.crypto.subtle.digest('SHA-256', buf).then(function (ab) {
        return Array.prototype.map.call(new Uint8Array(ab), function (b) {
          return b.toString(16).padStart(2, '0');
        }).join('');
      }).catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    try {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return iso; }
  }

  /* Validate one offer record against today. Returns null when OK, else an
     issue object { reason, title, msg }. `code` is the plaintext code from the
     cart tag (used only for display). */
  function validateOffer(rec, code) {
    if (!rec) {
      return { reason: 'unknown',
        title: ckT('ui.offer_guard.unknown_title', 'We can’t verify the offer on your cart'),
        msg: ckT('ui.offer_guard.unknown_msg', 'The code {code} on your cart items isn’t recognised anymore, so we can’t apply it at checkout.', { code: code }) };
    }
    if (rec.status === 'disabled') {
      return { reason: 'disabled',
        title: ckT('ui.offer_guard.disabled_title', 'This offer has been switched off'),
        msg: ckT('ui.offer_guard.disabled_msg', 'The offer {code} is no longer active, so it can’t be applied at checkout.', { code: code }) };
    }
    if (rec.valid_to) {
      var today = new Date(); today.setHours(0, 0, 0, 0);
      var t = new Date(rec.valid_to + 'T00:00:00'); t.setHours(0, 0, 0, 0);
      if (!isNaN(t.getTime()) && today > t) {
        return { reason: 'expired',
          title: ckT('ui.offer_guard.expired_title', 'The offer on your cart has expired'),
          msg: ckT('ui.offer_guard.expired_msg', 'The offer {code} expired on {date}, so it can’t be applied at checkout.', { code: code, date: fmtDate(rec.valid_to) }) };
      }
    }
    if (rec.usage_limit != null && (rec.times_used || 0) >= rec.usage_limit) {
      return { reason: 'used',
        title: ckT('ui.offer_guard.used_title', 'This offer has been fully redeemed'),
        msg: ckT('ui.offer_guard.used_msg', 'The offer {code} has reached its redemption limit, so it can’t be applied at checkout.', { code: code }) };
    }
    return null;
  }

  var REMOVE_FIX = ckT('ui.offer_guard.remove_fix', 'Open your cart, remove those items, then add them again from a current offer link — or message us on WhatsApp and we’ll sort it out.');
  var CONFLICT_FIX = ckT('ui.offer_guard.conflict_fix', 'Open your cart and keep items from just one offer — remove the rest — then check out.');

  /* ── state application ───────────────────────────────────────────── */
  function setIssue(issue) {
    if (issue) {
      root.setAttribute('data-ck-offer-issue', issue.reason || '1');
      renderBanner(issue);
      renderCartNotes(issue);
    } else {
      root.removeAttribute('data-ck-offer-issue');
      if (banner) banner.hidden = true;
      clearCartNotes();
    }
  }

  function renderBanner(issue) {
    if (!banner || onOfferPage) return;
    setText(banner, '[data-ck-guard-title]', issue.title);
    setText(banner, '[data-ck-guard-msg]', issue.msg);
    setText(banner, '[data-ck-guard-fix]', issue.fix || REMOVE_FIX);
    banner.hidden = false;
  }

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' })[c];
    });
  }

  function setText(scope, sel, txt) {
    var el = scope.querySelector(sel);
    if (el) el.textContent = txt || '';
  }

  /* Inline note dropped next to each hidden checkout button (drawer + /cart), so
     a customer who opens the cart still sees why checkout is unavailable. */
  var NOTE_CLASS = 'ck-offer-guard__cart-note';
  function checkoutButtons() {
    return document.querySelectorAll('button[name="checkout"], #checkout');
  }
  function renderCartNotes(issue) {
    checkoutButtons().forEach(function (btn) {
      var host = btn.parentNode;
      if (!host) return;
      var note = host.querySelector(':scope > .' + NOTE_CLASS);
      if (!note) {
        note = document.createElement('div');
        note.className = NOTE_CLASS;
        note.setAttribute('role', 'alert');
        note.innerHTML =
          '<p class="' + NOTE_CLASS + '-title"></p>' +
          '<p class="' + NOTE_CLASS + '-msg"></p>' +
          '<p class="' + NOTE_CLASS + '-fix"><strong>' + escHtml(ckT('ui.offer_guard.how_to_fix', 'How to fix:')) + '</strong> <span></span></p>' +
          '<a class="' + NOTE_CLASS + '-cta" href="' + CART_URL + '">' + escHtml(ckT('ui.offer_guard.review_cart', 'Review your cart')) + '</a>';
        host.insertBefore(note, btn);
      }
      note.querySelector('.' + NOTE_CLASS + '-title').textContent = issue.title;
      note.querySelector('.' + NOTE_CLASS + '-msg').textContent = issue.msg;
      note.querySelector('.' + NOTE_CLASS + '-fix span').textContent = issue.fix || REMOVE_FIX;
    });
  }
  function clearCartNotes() {
    var notes = document.querySelectorAll('.' + NOTE_CLASS);
    Array.prototype.forEach.call(notes, function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
  }

  /* ── main recompute (serialised) ─────────────────────────────────── */
  var running = false, pending = false;
  function recompute() {
    if (running) { pending = true; return; }
    running = true;
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var tags = {};
        (cart.items || []).forEach(function (it) {
          var offer = (it.properties && it.properties._ck_offer) || '';
          if (offer) tags[offer] = true;
        });
        var keys = Object.keys(tags);

        if (keys.length === 0) { setIssue(null); return; }

        if (keys.length > 1) {
          setIssue({
            reason: 'conflict',
            title: ckT('ui.offer_guard.conflict_title', 'Only one offer per order'),
            msg: ckT('ui.offer_guard.conflict_msg', 'Your cart mixes items from more than one offer, and only a single offer can apply to an order.'),
            fix: CONFLICT_FIX
          });
          return;
        }

        var tag = keys[0];
        var ci = tag.indexOf(':');
        var type = ci >= 0 ? tag.slice(0, ci) : '';
        var code = ci >= 0 ? tag.slice(ci + 1) : tag;

        if (type === 'airline') {
          var issue = validateOffer(airlineByCode[code.toLowerCase()], code);
          if (issue) issue.fix = REMOVE_FIX;
          setIssue(issue);
          return;
        }

        if (type === 'claim') {
          /* claims are indexed by SHA-256(lowercased code) */
          return sha256hex(code.toLowerCase()).then(function (hash) {
            var issue;
            if (!hash) {
              /* crypto unavailable → we can't verify, so play safe and block */
              issue = { reason: 'unverifiable',
                title: ckT('ui.offer_guard.unknown_title', 'We can’t verify the offer on your cart'),
                msg: ckT('ui.offer_guard.unverifiable_msg', 'We couldn’t verify the claim on your cart items in this browser.') };
            } else {
              issue = validateOffer(claimByHash[hash], code);
            }
            if (issue) issue.fix = REMOVE_FIX;
            setIssue(issue);
          });
        }

        /* Unrecognised tag prefix — don't block a checkout we don't understand. */
        setIssue(null);
      })
      .catch(function () { setIssue(null); })
      .then(finish, finish);

    function finish() {
      running = false;
      if (pending) { pending = false; recompute(); }
    }
  }

  /* ── triggers ────────────────────────────────────────────────────── */
  /* Hook fetch so any /cart mutation (add / change / update / clear) re-runs the
     guard, regardless of which theme component made the request. */
  var CART_MUTATOR = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
  if (window.fetch && !window.__ckOfferGuardHooked) {
    window.__ckOfferGuardHooked = true;
    var _fetch = window.fetch;
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      if (CART_MUTATOR.test(url)) {
        p.then(function () { setTimeout(recompute, 80); }, function () {});
      }
      return p;
    };
  }

  /* Horizon dispatches a CartUpdateEvent; listen for its likely names too. */
  ['cart:update', 'cart:updated', 'cart:refresh', 'CartUpdateEvent', 'variant:add'].forEach(function (name) {
    document.addEventListener(name, function () { setTimeout(recompute, 40); });
  });

  window.addEventListener('pageshow', function () { recompute(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') recompute();
  });

  /* Expose for manual re-checks (e.g. offer pages after they mutate the cart). */
  window.ckOfferGuardRecompute = recompute;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recompute);
  } else {
    recompute();
  }
})();
